/* eslint-disable max-classes-per-file */
import {
  SpraypaintBase,
  Model,
  MiddlewareStack,
  JsonapiResourceIdentifier,
} from 'spraypaint';
import { WritePayload } from 'spraypaint/lib-esm/util/write-payload';
import { CollectionProxy } from 'spraypaint/lib-esm/proxies';
import Cookies from 'js-cookie';
import {
  ModelAttributeChangeSet,
  PersistedSpraypaintRecord,
  SaveOptions,
} from 'spraypaint/lib-esm/model';
import { paramCase } from 'param-case';
import VueRouter, { Location, RawLocation } from 'vue-router';
import axios from 'axios';
import { RelChangeType } from '../../types';
import prompt from '../services/Prompt';
import AdminDelegate from '../delegates/AdminDelegate';

export type ProxyAppRec = CollectionProxy<ApplicationRecord>;

type ApplicationRecordConfig = {
  apiBase: string;
  apiNamespace?: string;
  useCookies?: boolean;
  appVersion: string;
  project: string;
};

let _config: ApplicationRecordConfig | null = null;

export function getCSRFHeaders(): Record<string, string> | undefined {
  const token = Cookies.get('csrftoken');
  if (!token) {
    return undefined;
  }
  return {
    'X-CSRFTOKEN': token,
  };
}

export const getModelType = (
  model: ApplicationRecord | typeof ApplicationRecord,
): string => {
  const klass =
    model instanceof ApplicationRecord
      ? model.klass
      : (model as typeof ApplicationRecord);
  if (!klass.jsonapiType) {
    throw new Error('Model has no jsonapiType set');
  }
  return klass.jsonapiType;
};

@Model()
export default class ApplicationRecord extends SpraypaintBase {
  // --- Spraypaint

  static baseUrl = '';

  static apiNamespace = '';

  static clientApplication = '';

  static url(id?: string | number, append = ''): string {
    return `${super.url(id)}/${append}`;
  }

  static generateAuthHeader(token: string) {
    return `Token ${token}`;
  }

  // --- setup

  static setup(config: ApplicationRecordConfig) {
    if (_config != null) {
      window.console.warn(
        'ApplicationRecord.setup() was already called before',
      );
    }
    const {
      apiBase,
      apiNamespace = '/backend/api/v1',
      useCookies = false,
      appVersion,
      project,
    } = config || {};
    _config = { apiBase, apiNamespace, useCookies, appVersion, project };
    ApplicationRecord.baseUrl = apiBase;
    ApplicationRecord.apiNamespace = apiNamespace;
    ApplicationRecord.clientApplication = `${project}-${appVersion}`;
  }

  // --- instance props

  declare klass: typeof ApplicationRecord;

  isRefOnly() {
    return this.id && Object.keys(this['_originalAttributes']).length === 0;
  }

  // --- instance getters

  /**
   * Returns the list of relationships' keys that will be serialized when
   * uploading a file
   */
  get uploadWithRelationshipKeys(): string[] {
    return [];
  }

  /**
   * Returns the name, used for a file upload
   */
  get uploadImageName(): string {
    return 'image';
  }

  /**
   * Returns the url, used for a file upload
   */
  get uploadUrl(): string {
    return this.klass.url();
  }

  // --- AdminDelegate

  // An admin delegate, used if the model is used within an admin interface
  static adminDelegate?: AdminDelegate;

  // --- static props

  static changesMaxCheckDepth = 3;

  // --- static methods for destruction

  static destroy<T extends ApplicationRecord>(item: T): Promise<T> {
    if (process.env.NODE_ENV === 'development') {
      window.console.log(
        `deleting item of type: ${item.resourceIdentifier.type} id: ${item.id}`,
      );
    }
    return item.destroy().then(() => item);
  }

  // Destroys many items and returns many promises
  static destroyMarkedItems<T extends ApplicationRecord>(
    list: T[],
  ): Promise<T>[] {
    const items = list.filter(item => item.id && item.isMarkedForDestruction);
    return items.map(ApplicationRecord.destroy);
  }

  // Destroys many items and returns one promise
  static destroyMarkedItemsSequentially<T extends ApplicationRecord>(
    list: T[],
  ): Promise<T[]> {
    const items = list.filter(item => item.id && item.isMarkedForDestruction);
    const result: T[] = [];
    const push = (item: T) => {
      result.push(item);
    };
    const queue = items.reduce((agg, item) => {
      return agg.then(() => {
        return ApplicationRecord.destroy(item).then(push);
      });
    }, Promise.resolve());
    return queue.then(() => result);
  }

  // Shows a prompt to ask before destroying the items sequentially
  static async destroyItems<T extends ApplicationRecord>(
    items: T[],
    options?: Partial<{ title: string; subtitle: string; btnTitle: string }>,
  ): Promise<false | T[]> {
    const btnTitle =
      options?.btnTitle ||
      (items.length > 1 ? `Delete (${items.length})` : 'Delete');
    const result = await prompt.delete({
      title: options?.title ?? 'Are you sure?',
      subtitle: options?.subtitle ?? 'This cannot be undone.',
      buttons: [
        prompt.cancelBtn,
        { key: 'delete', title: btnTitle, destroy: true },
      ],
    });
    if (result.isCancel) {
      return false;
    }
    items.forEach(item => {
      item.isMarkedForDestruction = true;
    });
    return ApplicationRecord.destroyMarkedItemsSequentially(items);
  }

  // --- instance methods for destruction

  // Sends a DELETE request at given subpath
  //
  // ```js
  // const inst = new User({ id: 'fake-1' });
  // inst.destroyWithSubpath('avatar/');
  // // Sends a DELETE request to /users/fake-1/avatar/
  // ```
  async destroyWithSubpath(subpath: string) {
    if (!subpath) {
      throw new Error('no subpath');
    }
    if (!this.id) {
      throw new Error('no id');
    }
    const url = (this.klass as typeof ApplicationRecord).url(this.id, subpath);
    await fetch(url, {
      method: 'DELETE',
      credentials: _config?.useCookies ? 'include' : 'omit',
      headers: getCSRFHeaders(),
    });
  }

  // Walks all relationships and destroys all that are marked for destruction
  //
  // Array relationships (aka Collections) are currently not supported.
  async destroyMarkedForDestructionRelationships() {
    const queue = Object.keys(this.relationships).map(key => {
      const item = this.relationships[key];
      if (!item) {
        return undefined;
      }
      if (Array.isArray(item)) {
        // TODO: handle relationship arrays
      } else if (item.id && item.isMarkedForDestruction) {
        // CHECK: should we use isMarkedForDisassociation instead?
        const subpath = `${paramCase(key)}/`;
        if (process.env.NODE_ENV === 'development') {
          const url = (this.klass as typeof ApplicationRecord).url(
            this.id,
            subpath,
          );
          window.console.log(
            `deleting item of type: ${item.resourceIdentifier.type} id: ${item.id} url: ${url}`,
          );
        }
        return this.destroyWithSubpath(subpath).then(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this as any)[key] = null;
        });
      }
      return undefined;
    });
    await Promise.all(queue);
  }

  // Saves item's changes, but first destroys relationships that were marked for destruction
  async save<I extends SpraypaintBase>(
    this: I,
    options?: SaveOptions<I>,
  ): Promise<boolean> {
    if (this instanceof ApplicationRecord) {
      await this.destroyMarkedForDestructionRelationships();
    }
    return super.save(options).then(result => {
      this.reset();
      return result;
    });
  }

  mergeDataFrom<I extends ApplicationRecord>(
    source: I,
    ignoredKeys: string[] = [],
  ) {
    const toHash = this as HashAny;
    const data: HashAny = {
      ...source.attributes,
      ...source.relationships,
    };
    const lookupSet = new Set<string>();
    ignoredKeys.forEach((key: string) => {
      lookupSet.add(key);
    });
    Object.keys(data).forEach((key: string) => {
      if (lookupSet.has(key)) {
        return;
      }
      toHash[key] = data[key];
    });
  }

  static async fetchSchema() {
    if (process.env.NODE_ENV === 'development') {
      const res = await fetch(
        `${ApplicationRecord.baseUrl}${ApplicationRecord.apiNamespace}/schema/`,
        {
          credentials: 'include',
          headers: getCSRFHeaders(),
        },
      );
      const text = await res.text();
      window.console.log(text);
    }
  }

  asJSON(relationships: string[] = []) {
    const dup = this.dup();
    if (dup.isPersisted) {
      dup.isPersisted = false;
    }
    if (Array.isArray(relationships)) {
      relationships.forEach(key => {
        // Ignore non-string relationship definitions as well as idOnly
        if (typeof key !== 'string' || key.indexOf('.') > -1) {
          return;
        }
        const rel = (dup as HashAny)[key];
        if (typeof rel?.dup === 'function') {
          const _relDup = rel.dup();
          if (_relDup.isPersisted) {
            _relDup.isPersisted = false;
          }
          (dup as HashAny)[key] = _relDup;
        }
      });
    }
    const payload = new WritePayload(dup, relationships);
    return payload.asJSON();
  }

  asPersisted<I extends SpraypaintBase>(this: I): PersistedSpraypaintRecord<I> {
    if (!this.id) {
      throw new Error('No id');
    }
    return this as PersistedSpraypaintRecord<I>;
  }

  changes(): ModelAttributeChangeSet<this> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const realChanges: Hash<any[]> = {};
    const maxDepth = this.klass.changesMaxCheckDepth;

    const changes = super.changes();
    Object.keys(changes).forEach(key => {
      const change = (changes as HashAny)[key];
      const [prev, current] = change;
      if (
        prev &&
        current &&
        ((typeof current === 'object' && '__ob__' in current) ||
          (typeof prev === 'object' && '__ob__' in prev))
      ) {
        if (!same(prev, current, maxDepth)) {
          realChanges[key] = change;
        }
      } else {
        realChanges[key] = change;
      }
    });

    return realChanges as ModelAttributeChangeSet<this>;
  }

  // Scans all relationships against their initial values
  //
  // It does a shallow scan and doesn't check each relationship's dirty state.
  relChanges() {
    const changes: Hash<
      [
        JsonapiResourceIdentifier[],
        JsonapiResourceIdentifier[],
        RelChangeType[],
      ]
    > = {};
    const { attributeList } = this.klass;
    Object.keys(attributeList).forEach(key => {
      const attr = attributeList[key];
      if (!attr.isRelationship) {
        return;
      }
      const value = (this as HashAny)[key] as
        | SpraypaintBase
        | SpraypaintBase[]
        | undefined;
      const list = value ? (Array.isArray(value) ? value : [value]) : [];
      const initialRI: JsonapiResourceIdentifier[] =
        (this as HashAny)._originalRelationships[key] || [];
      const currentRI = list.map(item => item.resourceIdentifier);
      const changeType = list.map(
        ({ isPersisted, isMarkedForDestruction }) => ({
          isPersisted,
          isMarkedForDestruction,
        }),
      );
      if (
        list.some(item => !item.isPersisted) ||
        list.some(item => item.isMarkedForDestruction) ||
        !same(initialRI, currentRI, 3)
      ) {
        changes[key] = [initialRI, currentRI, changeType];
      }
    });
    return changes;
  }

  /**
   * Generate the FormData to send during a file upload
   *
   * @param file      The file (or blob) to upload
   * @param name      The name of the field that should be used
   * @param fileName  The filename that should be used
   * @param payload   An additional JSON-API payload to append
   * @returns  An instance of FormData
   */
  getUploadFormData(
    file: File | Blob,
    name: string,
    fileName?: string,
    payload?: HashAny,
  ): FormData | undefined {
    const formData = new FormData();
    if (payload) {
      const attributes = this.getUploadAttributes(payload);
      const relationships = this.getUploadRelationships(payload);
      Object.keys(attributes).forEach(key => {
        formData.append(key, attributes[key]);
      });
      Object.keys(relationships).forEach(key => {
        formData.append(key, relationships[key]);
      });
    }
    formData.append(name, file, fileName);
    return formData;
  }

  /**
   * Generate the attributes to send along with an upload
   *
   * @param data  A JSON-API request payload
   * @returns  A key-value mapping to append to FormData
   */
  getUploadAttributes(data: HashAny): Record<string, string | Blob> {
    const result: Record<string, string> = {};
    const map = data.attributes as HashAny;
    if (map) {
      Object.keys(map).forEach(key => {
        let value = map[key];
        if (typeof value === 'object') {
          value = JSON.stringify(value);
        }
        result[key] = value;
      });
    }
    return result;
  }

  /**
   * Generate the relationships to send along with an upload
   *
   * @param data  A JSON-API request payload
   * @returns  A key-value mapping to append to FormData
   */
  getUploadRelationships(data: HashAny): Record<string, string | Blob> {
    const result: Record<string, string> = {};
    const map = data.relationships as HashAny;
    if (map) {
      Object.keys(map).forEach(key => {
        result[key] = JSON.stringify(map[key].data);
      });
    }
    return result;
  }

  /**
   * Uploads a given file
   *
   * @param file     The file to upload. Usually obtained from a change event of
   *                 an input[type=file] field
   * @param options
   * @param options.onUploadProgress  A callback while the upload is sending bytes
   * @param options.url  The url to upload to. Defaults to `this.uploadUrl`
   * @param options.with  A list of relationships (similar to save) to include.
   *                      Defaults to `this.uploadWithRelationshipKeys`
   * @param options.imageName  The name used for the image upload.
   *                           Defaults to `this.uploadImageName`
   * @param options.fileName  The file name used for the image upload.
   * @param options.headers  Additional headers needed for the upload.
   *                         By default we include CSRF headers
   * @param options.params   Any params needed for the upload
   * @param options.withCredentials  Indicates if cross-site Access-Control
   *                                 requests should include credentials.
   *                                 Defaults to `true`
   * @returns  this
   */
  async upload<I extends ApplicationRecord>(
    this: I,
    file: File | Blob,
    options?: Partial<{
      onUploadProgress: (progressEvent: ProgressEvent) => void;
      url: string;
      with: string[];
      imageName: string;
      fileName: string;
      headers: Hash<string>;
      params: Hash<string>;
      withCredentials: boolean;
    }>,
  ): Promise<I> {
    const postUrl = options?.url ?? this.uploadUrl;
    const imageName = options?.imageName ?? this.uploadImageName;
    const withRelKeys = options?.with ?? this.uploadWithRelationshipKeys;
    const payload = new WritePayload(this, withRelKeys);
    const { data } = payload.asJSON();
    const formData = this.getUploadFormData(
      file,
      imageName,
      options?.fileName,
      data,
    );
    const headers: Hash<string> = {
      'Accept': 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      ...getCSRFHeaders(),
      ...options?.headers,
    };
    const response = await axios.post(postUrl, formData, {
      headers,
      params: options?.params,
      onUploadProgress: options?.onUploadProgress,
      withCredentials: options?.withCredentials ?? true,
    });
    if (response.status === 202 || response.status === 204) {
      const { data: jsonPayload } = response;
      return this.klass.fromJsonapi(jsonPayload.data, jsonPayload);
    }
    const { data: jsonPayload } = response;
    const model = this.fromJsonapi(
      jsonPayload.data,
      jsonPayload,
      payload.includeDirective,
    );
    payload.postProcess();
    return model; // NOTE: essentially `this`
  }
}

// --- MiddlewareStack

interface ResponseExt extends Response {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: any[];
}

const middlewareStack = new MiddlewareStack();

// Add jsonAPI errors to response
middlewareStack.afterFilters.push((response: ResponseExt, json: HashAny) => {
  response.errors = json.errors || [];
});

// Add CSRF token
middlewareStack.beforeFilters.push((url, options) => {
  const headers = getCSRFHeaders();
  if (headers) {
    Object.assign(options.headers, headers);
  }
  options.credentials = _config?.useCookies ? 'include' : 'omit';
});

ApplicationRecord.middlewareStack = middlewareStack;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function same(one: any, another: any, maxDepth: number): boolean {
  if (!maxDepth || maxDepth < 0) {
    return false;
  }
  if (another == null && one == null) {
    return true;
  }
  if (another == null || one == null || typeof another !== typeof one) {
    return false;
  }
  if (Array.isArray(another) && Array.isArray(one)) {
    if (another.length !== one.length) {
      return false;
    }
    if (maxDepth - 1 === 0) {
      return false;
    }
    for (let idx = 0; idx < one.length; idx += 1) {
      if (!same(one[idx], another[idx], maxDepth - 1)) {
        return false;
      }
    }
    return true;
  }
  if (typeof another === 'object' && typeof one === 'object') {
    const _set = new Set<string>();
    Object.keys(another).forEach(k => _set.add(k));
    Object.keys(one).forEach(k => _set.add(k));
    const uniqueKeys = Array.from(_set);
    if (uniqueKeys.length) {
      if (maxDepth - 1 === 0) {
        return false;
      }
      for (let idx = 0; idx < uniqueKeys.length; idx += 1) {
        const k = uniqueKeys[idx];
        if (!same(one[k], another[k], maxDepth - 1)) {
          return false;
        }
      }
    }
    return true;
  }
  return another === one;
}

/**
 * Replaces all relationships that have a `clone` method with the result of
 * calling it
 *
 * A `HasMany` relationship will walk all items and call `clone` on each item
 * individually.
 */
export function cloneRelationships<T = ApplicationRecord>(
  object: T,
  noClone: Hash<boolean> = {},
) {
  const { attributeList } = (object as unknown as ApplicationRecord).klass;
  const relKeys = Object.keys(attributeList).filter(
    key => attributeList[key].isRelationship,
  );
  const hashMap = object as unknown as HashAny;
  relKeys.forEach(key => {
    if (noClone[key] === true) {
      return;
    }
    const value = hashMap[key];
    if (!value) {
      return;
    }
    if (Array.isArray(value)) {
      hashMap[key] = value.map(item => {
        if (typeof (item as HashAny).clone === 'function') {
          return item.clone();
        }
        return item;
      });
    } else if (typeof value.clone === 'function') {
      hashMap[key] = value.clone();
    }
  });
}

/**
 * Adds an Unauthorized (401) status code handler to the ApplicationRecord
 * middleware
 */
export function addUnauthorizedHandler(
  router: VueRouter,
  options?: { loginLocation?: RawLocation },
) {
  const { loginLocation } = options || {};
  let login: Location;
  if (loginLocation == null) {
    login = { name: 'Login' };
  } else if (typeof loginLocation === 'string') {
    login = { path: loginLocation };
  } else {
    login = loginLocation;
  }
  ApplicationRecord.middlewareStack.afterFilters.push(response => {
    if (response.status !== 401) {
      return;
    }
    // ApplicationRecord.jwt = undefined;
    const redirect = window.location.pathname;
    const { href } = router.resolve({
      ...login,
      query: { ...login.query, redirect },
    });
    // CHECK: should we use router.push/replace?
    window.location.href = href;
  });
}
